import { addDoc, collection, serverTimestamp } from "firebase/firestore";

import { auth } from "@/integrations/firebase/auth";
import { COLLECTIONS } from "@/integrations/firebase/collections";
import { db } from "@/integrations/firebase/firestore";
import {
  ClientErrorPayload,
  reportClientError,
  setClientErrorWriter,
} from "@/shared/services/clientErrorSink";

/**
 * Wire browser errors to Firestore (PRD J5, NAV-11).
 *
 * Two halves, and the second is the one that matters most. Giving the sink a
 * writer only helps errors that something already routed to `logError` — every
 * such call is inside a `catch`, so it covers the failures the app **expected**
 * and handled. The failures worth hearing about are the other kind: the throw
 * during render that blanks the page, and the promise nobody awaited. Neither
 * passes through a `catch`, so neither reaches `logError` at all, and the
 * Sentry ErrorBoundary that catches the render crash reports it to a DSN that
 * is empty. Without the listeners below, the headline case of this whole
 * feature — "the page went white" — would report nothing.
 *
 * Kept apart from the sink itself so that `logError`, which nearly every hook
 * calls, does not drag Firebase into the import graph of every test that
 * touches one. `index.tsx` calls this once, at startup.
 *
 * Installed in **every** environment, local included. An error path wired but
 * never exercised stays broken silently for months — which is exactly what
 * happened to the empty Sentry DSN — so a developer running the emulators
 * should be able to watch a `client_errors` document appear.
 */
export function installClientErrorSink(): void {
  setClientErrorWriter(writeClientError);
  installGlobalCapture();
}

function installGlobalCapture(): void {
  window.addEventListener("error", (event: ErrorEvent) => {
    // A resource that failed to load — an <img>, a <script> — raises this same
    // event with no `error` on it. There is no stack and no message worth
    // grouping, and a CDN 404 is not an application fault.
    if (!event.error) return;
    void reportClientError(event.error);
  });

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      // `reason` is whatever was rejected, very often not an Error — which the
      // sink handles, and which is why the stack may legitimately be empty.
      void reportClientError(event.reason);
    },
  );
}

/**
 * How long a browser error is kept.
 *
 * A stack trace carries the reporting account's uid, their browser, and the
 * page they were on *including its query string* — a list's filters are often
 * the whole bug, and they name tools and business units. That is personal data with a
 * purpose that expires: it is useful while somebody is diagnosing the bug and
 * useless afterwards. Thirty days is comfortably longer than any triage cycle
 * and short enough to be a real answer to "how long do you keep it".
 *
 * Enforced by a Firestore TTL policy on `expiresAt` (`firestore.tf`), so the
 * deletion happens whether or not anybody remembers.
 */
const RETENTION_DAYS = 30;

async function writeClientError(payload: ClientErrorPayload): Promise<void> {
  const user = auth.currentUser;
  // The rules pin `reportedById` to the caller, so a signed-out browser cannot
  // report at all. Returning here rather than attempting the write keeps the
  // pre-sign-in crash — the one this cannot capture — out of the logs as a
  // permission error, which would read as a fault of its own.
  if (!user) return;

  await addDoc(collection(db, COLLECTIONS.CLIENT_ERRORS), {
    ...payload,
    reportedById: user.uid,
    createdAt: serverTimestamp(),
    // Written by the client rather than stamped by the trigger, and the rules
    // bound it to at most 45 days out rather than pinning it exactly. They
    // cannot pin it: `createdAt` is a server timestamp the client cannot know,
    // so `createdAt + 30d` is not a value a client write can produce. A
    // *ceiling* is what retention actually needs anyway — a client that sets it
    // sooner deletes its own report sooner, which is nobody's problem; one that
    // tries to set it later is refused. Stamping it in the trigger instead
    // would double the write rate on the highest-volume collection in the app
    // to record something the client already knows.
    //
    // Note this date comes from the BROWSER's clock. That is why the rules
    // allow 45 days against the 30 written here: a machine set days fast would
    // otherwise have every report refused, silently, because the sink swallows
    // refusals on purpose.
    expiresAt: new Date(Date.now() + RETENTION_DAYS * 24 * 60 * 60 * 1000),
  });
}
